import { ActionContext, ActionTree } from 'vuex'
import { IProductExtended, IRootState, IShoppingCartState } from '@/@types'
import store from '@/store'
import CONSTANTS from '@/constants'
import { mixpanelEvent } from '@/helpers/mixins'

const actions: ActionTree<IShoppingCartState, IRootState> = {
  addProductToShoppingCart: (
    context: ActionContext<IShoppingCartState, IRootState>,
    product: IProductExtended
  ) => {
    const newShoppingCartState = context.state.shoppingCart
    newShoppingCartState.push(product)
    context.commit('SHOPPING_CART_UPDATED', newShoppingCartState)
    context.dispatch('setSelectedPlan', context.getters.selectedPlan)
    mixpanelEvent.methods.gaAddToCartEvent(product, context)
  },
  removeProductFromShoppingCart: (
    context: ActionContext<IShoppingCartState, IRootState>,
    product: IProductExtended
  ) => {
    const cartMerchantIds = context.state.shoppingCart.map(item => item.merchant_id)
    context.commit('SHOPPING_CART_REMOVED_ITEM_UPDATED', product)
    context.dispatch('setSelectedPlan', context.getters.selectedPlan)
    if (
      product.merchant_id !== CONSTANTS.PLANS.SKILL_DIVE_ANNUAL &&
      cartMerchantIds.includes(product.merchant_id)
    ) {
      mixpanelEvent.methods.gaRemoveFromCartEvent(product, context)
    }

    const couponStr = store.getters['coupons/getCoupon']
    store.dispatch('coupons/updateCouponObjStack', { root: true })
    store.dispatch('priceDetails/priceDetailsUpdate', { root: true })
    store.dispatch('recurly/createRecurlyInstance', { root: true })
    store.dispatch('recurly/setRecurlyCoupon', couponStr, { root: true })
    context.state.shoppingCart.forEach(cartItem => {
      store.dispatch('recurly/calculateRecurlyPricing', cartItem, { root: true })
    })
  },
  setBundleProductError: (context: ActionContext<IShoppingCartState, IRootState>, payload) => {
    context.commit('BUNDLE_PRODUCT_ERROR_UPDATED', payload)
  },
  setSelectedPlan: (
    context: ActionContext<IShoppingCartState, IRootState>,
    product: IProductExtended
  ) => {
    context.commit('SHOPPING_CART_SELECTED_PLAN_UPDATED', product)
  },
  setAddOnscertificationsList: (
    context: ActionContext<IShoppingCartState, IRootState>,
    list: IProductExtended[]
  ) => {
    context.commit('ADD_ONS_CERTIFICATIONS_LIST_UPDATED', list)
  },
  getVoucherAvailability: (
    context: ActionContext<IShoppingCartState, IRootState>,
    certification: IProductExtended
  ) => {
    const isBusinessUser = store.getters['business/isBusinessUser']
    const isBusinessPlanExpired = store.getters['business/isBusinessPlanExpired']
    const userHasSubscription = store.getters['subscriptions/getUserHasSubscription']
    const cartHasPremiumSubscription = store.getters['shoppingCart/cartHasPremiumSubscription']
    const cartHasFundamentalsAnnualSubscription =
      store.getters['shoppingCart/cartHasFundamentalsAnnualSubscription']
    const cartHasFundamentalsMonthlySubscription =
      store.getters['shoppingCart/cartHasFundamentalsMonthlySubscription']
    // Extract voucher IDs from constants and define the conditional certifications list
    const {
      VOUCHERS: { EJPT, EDA, EWPT, EWPTX, ECPPT, ECPPT3 },
    } = CONSTANTS
    const conditionalPurchasableCertifications = [EJPT, EDA, EWPT, EWPTX, ECPPT, ECPPT3]

    // If the user does not have a subscription but has a subscription in their cart,
    // they are allowed access to the voucher.
    if (
      !userHasSubscription &&
      (cartHasPremiumSubscription ||
        cartHasFundamentalsAnnualSubscription ||
        cartHasFundamentalsMonthlySubscription)
    ) {
      return true
    }

    // Determine if the current certification is one that has additional conditions.
    const isConditionalCertification = conditionalPurchasableCertifications.includes(
      certification.merchant_id
    )

    // For conditional certifications, check if the user either has a subscription
    // or qualifies as a business user with a valid (non-expired) plan.
    if (isConditionalCertification) {
      return userHasSubscription || (isBusinessUser && !isBusinessPlanExpired)
    }

    // For all other cases, the voucher is available.
    return true
  },

  addAddonsToPlanInCart: (
    context: ActionContext<IShoppingCartState, IRootState>,
    { plan, addOn }: { plan: IProductExtended; addOn: IProductExtended }
  ) => {
    context.commit('ADD_ADDONS_TO_PLAN_IN_CART', { plan, addOn })
  },
  removeAddonsFromPlanInCart: (
    context: ActionContext<IShoppingCartState, IRootState>,
    { plan, addOn }: { plan: IProductExtended; addOn: IProductExtended }
  ) => {
    context.commit('REMOVE_ADDONS_TO_PLAN_IN_CART', { plan, addOn })
  },
  setselectedAddOnsByUser: (
    context: ActionContext<IShoppingCartState, IRootState>,
    selectedAddOnsByUser: boolean
  ) => {
    context.commit('SET_SELECTED_ADDONS_BY_USER', selectedAddOnsByUser)
  },
}

export default actions
